import { useState, useEffect, useRef, useCallback } from 'react';
import { getRulesetType, updateRulesetType } from '../services/api/rulesetApi';

const MAX_LENGTH = 16000;

/**
 * Custom hook for managing the Global Prompt Context field on a rule set.
 * Fetches the current rule set data and provides save functionality that
 * persists changes via the existing PUT /api/ruleset-types/{id} endpoint.
 *
 * Follows the useAutoExportSettings hook pattern.
 *
 * @param {string} rulesetId - The ID of the rule set
 * @returns {Object} Global prompt context state and controls
 */
export const useGlobalPromptContext = (rulesetId) => {
  const [globalPromptContext, setGlobalPromptContext] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const rulesetRef = useRef(null);
  const hasFetched = useRef(false);

  // Reset fetch guard when rulesetId changes
  useEffect(() => {
    hasFetched.current = false;
  }, [rulesetId]);

  useEffect(() => {
    if (!hasFetched.current && rulesetId) {
      hasFetched.current = true;
      fetchRuleset();
    }
  }, [rulesetId]);

  const fetchRuleset = async () => {
    if (!rulesetId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await getRulesetType(rulesetId);
      const data = response.data;
      rulesetRef.current = data;
      setGlobalPromptContext(data.globalPromptContext ?? '');
    } catch (err) {
      setError('Failed to load Global Prompt Context');
      console.error('Error fetching Global Prompt Context:', err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Save the global prompt context value to the API.
   * Sends the full ruleset object with the updated globalPromptContext field.
   */
  const saveGlobalPromptContext = useCallback(async (value) => {
    if (!rulesetRef.current || saving) return;

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const updatedRuleset = {
        ...rulesetRef.current,
        globalPromptContext: value
      };
      const response = await updateRulesetType(rulesetRef.current.id, updatedRuleset);
      rulesetRef.current = response.data;
      setSuccess('Global Prompt Context saved successfully');

      // Clear success after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to save Global Prompt Context');
      console.error('Error saving Global Prompt Context:', err);
    } finally {
      setSaving(false);
    }
  }, [saving]);

  const remainingChars = MAX_LENGTH - (globalPromptContext?.length || 0);

  const clearError = useCallback(() => setError(null), []);
  const clearSuccess = useCallback(() => setSuccess(null), []);

  return {
    globalPromptContext,
    setGlobalPromptContext,
    remainingChars,
    loading,
    saving,
    error,
    success,
    saveGlobalPromptContext,
    clearError,
    clearSuccess
  };
};
